import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, CalendarDays, MapPin, CalendarCheck, X } from "lucide-react";
import { api } from "@/api";
import { C, STAGE_COLORS } from "@/theme";
import { stageForRun, stageLabel } from "@/pathway";
import { fmtDate } from "@/format";
import { Badge, Button, Card, SectionTitle, CardSub, StarRating, Modal, Field } from "@/ui";

// --------------------------- Pathway Timeline -------------------------------
const STAGE_GROUPS = [
  { stage: "Assessment", runs: [1] },
  { stage: "Training", runs: [2, 3, 4, 5, 6, 7, 8, 9, 10] },
  { stage: "Check Ride", runs: [11] },
  { stage: "Targeted Training", runs: [12, 13, 14] },
  { stage: "Final Assessment", runs: [15] },
];

function outcomeText(run) {
  return run.outcome_level || run.pass_fail || run.assessment?.outcome || "Recorded";
}

export function PathwayTimeline({ runs, nextRunNumber, pathwayStatus, variant = "compact", onPressRun, extraUnlocked = false }) {
  const runByNum = new Map();
  runs.forEach((r) => { if (r.run_number) runByNum.set(r.run_number, r); });
  const active = pathwayStatus !== "Disabled" && pathwayStatus !== "Approved for Test";
  const fastTracked = !!runByNum.get(11)?.fast_track_to_test;
  const statusFor = (n) => {
    if (runByNum.has(n)) return "done";
    if (fastTracked && n >= 12 && n <= 14) return "skipped";
    if (active && n === nextRunNumber) return "current";
    return "upcoming";
  };

  if (variant === "compact") {
    return (
      <div className="flex items-center overflow-x-auto py-2">
        {Array.from({ length: extraUnlocked ? 20 : 15 }, (_, i) => i + 1).map((n, idx) => {
          const st = statusFor(n);
          const color = STAGE_COLORS[stageForRun(n)] ?? C.brandPrimary;
          const filled = st === "done";
          return (
            <div key={n} className="flex items-center" data-testid={`timeline-node-${n}`}>
              {idx > 0 ? <div style={{ width: 16, height: 3, background: runByNum.has(n - 1) ? color : C.border }} className="rounded" /> : null}
              <div style={{ background: filled ? color : C.surfaceTertiary, borderColor: st === "current" ? color : filled ? color : C.border, borderWidth: st === "current" ? 3 : 2, borderStyle: st === "skipped" ? "dashed" : "solid" }}
                className="w-[30px] h-[30px] rounded-full flex items-center justify-center shrink-0">
                {filled ? <Check size={12} color="#fff" strokeWidth={3} /> : <span style={{ color: st === "current" ? color : C.muted, textDecoration: st === "skipped" ? "line-through" : "none" }} className="text-xs font-extrabold">{n}</span>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {STAGE_GROUPS.map((group) => {
        const color = STAGE_COLORS[group.stage] ?? C.brandPrimary;
        return (
          <div key={group.stage} className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <span style={{ background: color }} className="w-2.5 h-2.5 rounded-full" />
              <span style={{ color: C.onSurface }} className="text-sm font-extrabold">{stageLabel(group.stage)}</span>
            </div>
            {group.runs.map((n) => {
              const st = statusFor(n);
              const run = runByNum.get(n);
              const skipped = st === "skipped";
              const clickable = run && onPressRun;
              return (
                <button type="button" key={n} data-testid={`timeline-step-${n}`} disabled={!clickable} onClick={() => run && onPressRun(run)}
                  style={{ opacity: skipped ? 0.5 : 1 }} className={`flex items-center gap-3 text-left ${clickable ? "hover:bg-[#EAF0F8] rounded-lg -mx-1 px-1 py-1" : ""}`}>
                  <span style={{ background: st === "done" ? color : C.surfaceTertiary, borderColor: st === "current" ? color : st === "done" ? color : C.border, borderWidth: st === "current" ? 3 : 2, borderStyle: skipped ? "dashed" : "solid" }}
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                    {st === "done" ? <Check size={13} color="#fff" strokeWidth={3} /> : <span style={{ color: st === "current" ? color : C.muted }} className="text-xs font-extrabold">{n}</span>}
                  </span>
                  <div className="flex-1">
                    <p style={{ color: C.onSurface, textDecoration: skipped ? "line-through" : "none" }} className="text-sm font-bold">Run {n}{st === "current" ? "  ·  Next up" : ""}</p>
                    {run ? (
                      <p style={{ color: C.onSurfaceSecondary }} className="text-xs mt-0.5">{outcomeText(run)} · {fmtDate(run.date)}</p>
                    ) : (
                      <p style={{ color: C.muted }} className="text-xs mt-0.5">{skipped ? "Not required" : st === "current" ? "Ready to record" : "Not yet reached"}</p>
                    )}
                  </div>
                  {clickable ? <ChevronRight size={16} color={C.muted} /> : null}
                </button>
              );
            })}
          </div>
        );
      })}
      {extraUnlocked ? (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <span style={{ background: C.brandPrimary }} className="w-2.5 h-2.5 rounded-full" />
            <span style={{ color: C.onSurface }} className="text-sm font-extrabold">Extra Training (Runs 16–20)</span>
          </div>
          {[16, 17, 18, 19, 20].map((n) => {
            const st = statusFor(n);
            const run = runByNum.get(n);
            const clickable = run && onPressRun;
            return (
              <button type="button" key={n} data-testid={`timeline-step-${n}`} disabled={!clickable} onClick={() => run && onPressRun(run)}
                className={`flex items-center gap-3 text-left ${clickable ? "hover:bg-[#EAF0F8] rounded-lg -mx-1 px-1 py-1" : ""}`}>
                <span style={{ background: st === "done" ? C.brandPrimary : C.surfaceTertiary, borderColor: st === "current" ? C.brandPrimary : st === "done" ? C.brandPrimary : C.border, borderWidth: st === "current" ? 3 : 2 }}
                  className="w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0">
                  {st === "done" ? <Check size={13} color="#fff" strokeWidth={3} /> : <span style={{ color: st === "current" ? C.brandPrimary : C.muted }} className="text-xs font-extrabold">{n}</span>}
                </span>
                <div className="flex-1">
                  <p style={{ color: C.onSurface }} className="text-sm font-bold">Run {n}{st === "current" ? "  ·  Next up" : ""}</p>
                  {run ? <p style={{ color: C.onSurfaceSecondary }} className="text-xs mt-0.5">{outcomeText(run)} · {fmtDate(run.date)}</p> : <p style={{ color: C.muted }} className="text-xs mt-0.5">{st === "current" ? "Ready to record" : "Optional extra run"}</p>}
                </div>
                {clickable ? <ChevronRight size={16} color={C.muted} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <span style={{ background: STAGE_COLORS["Approved for Test"] }} className="w-2.5 h-2.5 rounded-full" />
          <span style={{ color: C.onSurface }} className="text-sm font-extrabold">Official Test</span>
        </div>
        <div className="flex items-center gap-3">
          <span style={{ background: pathwayStatus === "Approved for Test" ? STAGE_COLORS["Approved for Test"] : C.surfaceTertiary, borderColor: pathwayStatus === "Approved for Test" ? STAGE_COLORS["Approved for Test"] : C.border }}
            className="w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0">
            {pathwayStatus === "Approved for Test" ? <Check size={13} color="#fff" strokeWidth={3} /> : <span style={{ color: C.muted }} className="text-xs font-extrabold">✓</span>}
          </span>
          <div className="flex-1">
            <p style={{ color: C.onSurface }} className="text-sm font-bold">Approved for Test</p>
            <p style={{ color: C.muted }} className="text-xs mt-0.5">{pathwayStatus === "Approved for Test" ? "Rider is ready to book" : "Complete Run 15 first"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------- Run Details (read-only) ------------------------
function weatherLabel(w) { return Array.isArray(w) ? w.join(", ") : w ? String(w) : ""; }
function Meta({ label, value }) {
  return (
    <div style={{ background: C.surfaceTertiary }} className="flex-1 rounded-lg p-2.5">
      <p style={{ color: C.muted }} className="text-[11px] font-semibold">{label}</p>
      <p style={{ color: C.onSurface }} className="text-[13px] font-bold mt-0.5">{value}</p>
    </div>
  );
}
function SecTitle({ children }) {
  return <p style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-extrabold uppercase tracking-wide">{children}</p>;
}
function Body({ children }) {
  return <p style={{ color: C.onSurfaceSecondary }} className="text-[13px] leading-relaxed">{children}</p>;
}

export function RunDetails({ run }) {
  const isAssessment = run.stage === "Assessment";
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex gap-2.5"><Meta label="Conducted by" value={run.trainer_name ?? "—"} /><Meta label="Date" value={fmtDate(run.date)} /></div>
      <div className="flex gap-2.5"><Meta label="Duration" value={`${run.duration_minutes} min`} /><Meta label="Distance" value={run.distance_miles ? `${run.distance_miles} mi` : "—"} /></div>
      {run.location || weatherLabel(run.weather) ? (
        <div className="flex gap-2.5"><Meta label="Meeting point" value={run.location || "—"} /><Meta label="Weather" value={weatherLabel(run.weather) || "—"} /></div>
      ) : null}
      {run.eyesight_check ? <div className="flex items-center gap-2"><Check size={16} color={C.success} strokeWidth={3} /><span style={{ color: C.onSurface }} className="text-[13px]">Eyesight distance check completed</span></div> : null}

      {isAssessment && run.assessment ? (
        <div className="flex flex-col gap-1.5">
          <SecTitle>New-joiner Checklist</SecTitle>
          {Object.entries(run.assessment.checklist ?? {}).map(([k, v]) => (
            <div key={k} className="flex justify-between items-center py-0.5"><span style={{ color: C.onSurface }} className="text-[13px]">{k}</span>{v ? <Check size={16} color={C.success} strokeWidth={3} /> : <X size={16} color={C.muted} />}</div>
          ))}
          {run.assessment.outcome ? <Badge label={run.assessment.outcome} variant="success" /> : null}
        </div>
      ) : null}

      {!isAssessment && run.stage !== "Check Ride" && Object.keys(run.skills ?? {}).length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <SecTitle>Skills Checklist</SecTitle>
          {Object.entries(run.skills).map(([k, v]) => (
            <div key={k} className="flex justify-between items-center py-0.5"><span style={{ color: C.onSurface }} className="text-[13px]">{k}</span><StarRating value={v} size={14} /></div>
          ))}
        </div>
      ) : null}

      {run.ipsga ? (
        <div className="flex flex-col gap-1.5">
          <SecTitle>IPSGA Assessment</SecTitle>
          {[["Information", run.ipsga.information], ["Position", run.ipsga.position], ["Speed", run.ipsga.speed], ["Gear", run.ipsga.gear], ["Acceleration (Throttle)", run.ipsga.acceleration], ["Braking", run.ipsga.braking], ["Cornering", run.ipsga.cornering], ["Hazard Awareness", run.ipsga.hazard_awareness]].map(([label, val]) => (
            <div key={label} className="flex justify-between items-center py-0.5"><span style={{ color: C.onSurface }} className="text-[13px]">{label}</span><StarRating value={val} size={14} /></div>
          ))}
          {run.ipsga.notes ? <Body>{run.ipsga.notes}</Body> : null}
        </div>
      ) : null}

      {run.outcome_level ? <div className="flex flex-col gap-1.5"><SecTitle>Run Outcome</SecTitle><div><Badge label={run.outcome_level} variant="brand" /></div></div> : null}
      {run.stage === "Check Ride" ? <div className="flex flex-col gap-1.5"><SecTitle>Progression</SecTitle><div><Badge label={run.fast_track_to_test ? "To Test — skips Runs 12–14" : "Requires Targeted Stages (12–14)"} variant={run.fast_track_to_test ? "success" : "brand"} /></div></div> : null}
      {run.strengths ? <div className="flex flex-col gap-1.5"><SecTitle>Strengths</SecTitle><Body>{run.strengths}</Body></div> : null}
      {run.next_steps ? <div className="flex flex-col gap-1.5"><SecTitle>Action Plan</SecTitle><Body>{run.next_steps}</Body></div> : null}
      {run.trainer_notes ? <div className="flex flex-col gap-1.5"><SecTitle>{isAssessment ? "Assessor Notes" : "Notes"}</SecTitle><Body>{run.trainer_notes}</Body></div> : null}
    </div>
  );
}

// --------------------------- Booking sections -------------------------------
const todayKey = () => new Date().toISOString().slice(0, 10);
function statusVariant(s) { return s === "Accepted" ? "success" : s === "Pending" ? "warning" : s === "Declined" ? "error" : "neutral"; }

function RequestMeta({ r, showRider }) {
  return (
    <div className="flex-1 flex flex-col gap-1">
      <p style={{ color: C.onSurface }} className="text-sm font-extrabold">{showRider ? r.rider_name : r.trainer_name}</p>
      <div className="flex items-center gap-1.5"><CalendarDays size={13} color={C.muted} /><span style={{ color: C.muted }} className="text-xs font-semibold">{fmtDate(r.date)}{r.time ? ` · ${r.time}` : ""}</span></div>
      {r.meeting_point ? <div className="flex items-center gap-1.5"><MapPin size={14} color={C.brandPrimary} /><span style={{ color: C.muted }} className="text-xs font-semibold">{r.meeting_point}{r.meeting_postcode ? ` · ${r.meeting_postcode}` : ""}</span></div> : null}
      {r.note ? <p style={{ color: C.onSurfaceSecondary }} className="text-[13px] italic mt-0.5">“{r.note}”</p> : null}
      {r.status === "Declined" && r.decline_reason ? <p style={{ color: C.error }} className="text-xs font-semibold mt-0.5">Declined: {r.decline_reason}</p> : null}
    </div>
  );
}

export function RiderBookingSection({ riderId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["run-requests", "rider", riderId], queryFn: () => api.get(`/run-requests?rider_id=${riderId}`), enabled: !!riderId });
  const del = useMutation({ mutationFn: (id) => api.del(`/run-requests/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["run-requests"] }) });
  const reqs = q.data ?? [];
  return (
    <Card testID="rider-booking-card">
      <div className="flex items-center gap-2"><CalendarCheck size={18} color={C.brandPrimary} /><SectionTitle>My Training Bookings</SectionTitle></div>
      <CardSub>Request a training run with your tutor</CardSub>
      <div className="mt-3 w-56"><Button label="Book a Training Run" testID="book-run-btn" onClick={() => navigate("/book-run")} /></div>
      {reqs.length === 0 ? <CardSub>No bookings yet — tap above to request one.</CardSub> : (
        <div className="mt-2 divide-y" style={{ borderColor: C.divider }}>
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-3" data-testid={`rider-request-${r.id}`}>
              <RequestMeta r={r} />
              <div className="flex flex-col items-end gap-1.5">
                <Badge label={r.status === "Accepted" ? "Scheduled" : r.status} variant={statusVariant(r.status)} />
                {r.status === "Pending" ? <button data-testid={`rider-cancel-${r.id}`} onClick={() => del.mutate(r.id)} style={{ background: C.surfaceTertiary, color: C.error }} className="px-3 py-1.5 rounded-lg text-xs font-bold">Cancel</button> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function TrainerBookingSection({ trainerId }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [declineFor, setDeclineFor] = useState(null);
  const [reason, setReason] = useState("");
  const q = useQuery({ queryKey: ["run-requests", "trainer", trainerId], queryFn: () => api.get(`/run-requests?trainer_id=${trainerId}`), enabled: !!trainerId });
  const respond = useMutation({
    mutationFn: (v) => api.put(`/run-requests/${v.id}/respond`, { status: v.status, decline_reason: v.decline_reason ?? "" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["run-requests"] }),
  });
  const reqs = q.data ?? [];
  const pending = reqs.filter((r) => r.status === "Pending");
  const scheduled = reqs.filter((r) => (r.status === "Accepted" && r.date.slice(0, 10) >= todayKey()) || r.status === "Completed");
  const submitDecline = () => { if (declineFor) respond.mutate({ id: declineFor, status: "Declined", decline_reason: reason.trim() }); setDeclineFor(null); setReason(""); };
  return (
    <>
      <Card testID="trainer-booking-card">
        <div className="flex items-center gap-2"><CalendarCheck size={18} color={C.warning} /><SectionTitle>Booking Requests</SectionTitle></div>
        <CardSub>Riders requesting a training run with you</CardSub>
        {pending.length === 0 ? <CardSub>No pending requests</CardSub> : (
          <div className="mt-2 divide-y" style={{ borderColor: C.divider }}>
            {pending.map((r) => (
              <div key={r.id} className="py-3 flex flex-col gap-2.5" data-testid={`trainer-request-${r.id}`}>
                <RequestMeta r={r} showRider />
                <div className="flex gap-2.5">
                  <button data-testid={`accept-${r.id}`} onClick={() => respond.mutate({ id: r.id, status: "Accepted" })} style={{ background: C.success, color: "#fff" }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-extrabold"><Check size={15} strokeWidth={3} />Accept</button>
                  <button data-testid={`decline-${r.id}`} onClick={() => { setDeclineFor(r.id); setReason(""); }} style={{ background: C.surfaceTertiary, color: C.error }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-extrabold"><X size={15} strokeWidth={3} />Decline</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {scheduled.length > 0 ? (
        <Card testID="trainer-scheduled-card">
          <div className="flex items-center gap-2"><CalendarCheck size={18} color={C.success} /><SectionTitle>Scheduled Runs</SectionTitle></div>
          <CardSub>Accepted bookings coming up</CardSub>
          <div className="mt-2 divide-y" style={{ borderColor: C.divider }}>
            {scheduled.map((r) => {
              const done = r.status === "Completed";
              const isToday = r.date.slice(0, 10) === todayKey();
              return (
                <div key={r.id} className="py-3 flex flex-col gap-2.5" data-testid={`trainer-scheduled-${r.id}`}>
                  <div className="flex items-center gap-3"><RequestMeta r={r} showRider /><Badge label={done ? "Recorded" : isToday ? "Today" : "Scheduled"} variant={done ? "neutral" : isToday ? "warning" : "success"} /></div>
                  {!done ? <button data-testid={`record-run-${r.id}`} onClick={() => navigate(`/run-form?riderId=${r.rider_id}&date=${r.date}&location=${encodeURIComponent(r.meeting_point ?? "")}&postcode=${encodeURIComponent(r.meeting_postcode ?? "")}&bookingId=${r.id}`)} style={{ background: C.brandPrimary, color: "#fff" }} className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-extrabold"><Check size={15} strokeWidth={3} />Record Run</button> : null}
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}
      <Modal open={!!declineFor} onClose={() => setDeclineFor(null)} title="Decline request" testID="decline-modal">
        <p style={{ color: C.muted }} className="text-sm mb-3">Let the rider know why (a date clash, etc.)</p>
        <Field value={reason} onChange={setReason} placeholder="Reason" multiline testID="decline-reason-input" />
        <div className="flex gap-3 mt-4">
          <Button label="Cancel" variant="secondary" onClick={() => setDeclineFor(null)} testID="decline-cancel" />
          <Button label="Decline" variant="danger" onClick={submitDecline} disabled={!reason.trim()} testID="decline-confirm" />
        </div>
      </Modal>
    </>
  );
}
